// The curtain's ⋮ entry: Reverse open / close (and "Reverse gate" on a sliding
// gate, 37). One hook, both shells — the phone screen feeds it to DeviceAppBar's
// `extraItems`, the desktop screen and the room card to DeviceCardMenu's.
//
// Shaped exactly like the white light's menu (ui/shared/controls/LightActions
// `useLightMenu`), because it is the same kind of setting — a per-unit wiring
// correction, owner or Super Admin only — and two installer toggles that behave
// differently would be two things to learn.
//
// ⚠️ WHAT IT DOES IS NOT WHAT THE LIGHT'S DOES. The device performs this swap
// itself; the app never exchanges the two directions. See
// core/devices/curtainReverse for why that is not a choice.
//
// The answer is a TOAST, not a dialog (ui/shared/toast) — it only informs, and
// the operator is usually on their way back to the room.

import React, { useEffect, useState } from 'react';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useApp } from '../../../core/state/AppContext';
import { curtainNoun } from '../../../core/devices/curtain';
import {
  cachedCurtainOwner, getCurtainReversed, onCurtainReverseChange, setCurtainReversed,
} from '../../../core/devices/curtainReverse';
import { showToast } from '../toast';

/**
 * -> { items, reversed, noun, busy }
 *
 * `items` is the neutral `{ key, label, secondary, icon, onClick }` shape each
 * menu maps to its own, as `useLightMenu` returns.
 */
export default function useCurtainMenu(device) {
  const { isSuperAdmin, realUserId, userId } = useApp();
  const uno = device?.UniqueNumber;
  const noun = curtainNoun(device?.DeviceID ?? device?.DeviceId);
  const [reversed, setReversed] = useState(false);
  const [owner, setOwner] = useState(() => device?.MasterUserId ?? cachedCurtainOwner(uno));
  const [busy, setBusy] = useState(false);

  // The owner rides along with the install row the flag is read from — one call,
  // not two, exactly as the light's does.
  useEffect(() => {
    if (!uno) return undefined;
    let on = true;
    getCurtainReversed(device).then((r) => {
      if (!on) return;
      setReversed(!!r);
      if (owner == null) setOwner(cachedCurtainOwner(uno));
    });
    const off = onCurtainReverseChange((u, r) => {
      if (on && String(u) === String(uno)) setReversed(!!r);
    });
    return () => { on = false; off(); };
  }, [uno]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOwner = isSuperAdmin || (owner != null && String(owner) === String(realUserId));

  const items = [];
  if (isOwner) {
    items.push({
      key: 'reverse',
      label: reversed ? 'Open / close reversed ✓' : 'Reverse open / close',
      secondary: reversed
        ? 'Tap to set back to normal'
        : `If Open closes this ${noun.toLowerCase()} and Close opens it`,
      icon: <SwapHorizIcon fontSize="small" />,
      disabled: busy,
      onClick: async () => {
        const next = !reversed;
        setBusy(true);
        showToast(next ? `Reversing the ${noun.toLowerCase()}…` : 'Setting back to normal…');
        const r = await setCurtainReversed(uno, next, userId);
        setBusy(false);
        if (!r.ok) {
          // The flag did not save, so nothing changed — put the switch back.
          setReversed(reversed);
          showToast(r.message, { severity: 'error' });
          return;
        }
        showToast(r.message, { severity: r.confirmed ? 'success' : 'warning' });
      },
    });
  }
  return { items, reversed, noun, busy };
}
